/* istanbul ignore file - better tested visually */

import { classNames } from '@partful/shared/lib/classNames';
import { ComponentProps, forwardRef, useCallback, useEffect, useRef } from 'react';
import { Surface } from '../Surface';
import styles from './Dialog.module.scss';

type DialogProps = { modal?: boolean; disableCloseOnEscape?: boolean } & ComponentProps<'dialog'>;

export function Dialog({
  open,
  modal = false,
  disableCloseOnEscape = false,
  className,
  children,
  ...rest
}: DialogProps) {
  const dialogRef = useRef<HTMLDialogElement>(null);

  const openDialog = useCallback(() => {
    if (!dialogRef.current?.open) {
      dialogRef.current?.[modal === false ? 'show' : 'showModal']();
    }
  }, [modal]);

  const closeDialog = useCallback(() => {
    if (dialogRef.current?.open) {
      dialogRef.current.close();
    }
  }, []);

  const handleEscape = (e: KeyboardEvent) => {
    if (dialogRef.current?.open && e.key === 'Escape') {
      e.preventDefault();
    }
  };

  useEffect(() => {
    if (disableCloseOnEscape) document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  });

  useEffect(() => {
    if (open) {
      openDialog();
    } else {
      closeDialog();
    }
  }, [closeDialog, open, openDialog]);

  return (
    <dialog {...rest} ref={dialogRef} className={classNames(styles.dialog, className)}>
      {children}
    </dialog>
  );
}

type DialogPanelProps = ComponentProps<'div'>;
export const DialogPanel = forwardRef<HTMLDivElement, DialogPanelProps>(({ children, className, ...rest }, ref) => (
  <Surface {...rest} shadow="large" className={classNames(styles.dialogPanel, className)} ref={ref}>
    {children}
  </Surface>
));

type DialogHeaderProps = ComponentProps<'div'>;
export const DialogHeader = forwardRef<HTMLDivElement, DialogHeaderProps>(({ children, className, ...rest }, ref) => (
  <div {...rest} className={classNames(styles.dialogHeader, className)} ref={ref}>
    {children}
  </div>
));

type DialogContentProps = ComponentProps<'div'>;
export const DialogContent = forwardRef<HTMLDivElement, DialogContentProps>(({ children, className, ...rest }, ref) => (
  <div {...rest} className={classNames(styles.dialogContent, className)} ref={ref}>
    {children}
  </div>
));

type DialogFooterProps = ComponentProps<'div'>;
export const DialogFooter = forwardRef<HTMLDivElement, DialogFooterProps>(({ children, className, ...rest }, ref) => (
  <div {...rest} className={classNames(styles.dialogFooter, className)} ref={ref}>
    {children}
  </div>
));
